import Slider from "@stardust/slider";
import { registerThemeEditorSection } from "@stardust/theme-editor";
import { initProductCards } from "@stardust/product-card";
import { buildProductCard } from "@stardust/product-card-builder";

// Recently viewed storage manager - read-only for display purposes
// Products are tracked by product.js on product pages
const RecentlyViewedStorage = {
  STORAGE_KEY: "recently_viewed_products",
  MAX_ITEMS: 12,

  get() {
    try {
      const data = sessionStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('[RecentlyViewed] Error reading products:', error);
      return [];
    }
  },

  add(productHandle) {
    try {
      let products = this.get();

      // Remove if already exists (to move to front)
      products = products.filter(h => h !== productHandle);

      // Add to front
      products.unshift(productHandle);

      // Limit to max items
      products = products.slice(0, this.MAX_ITEMS);

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(products));
    } catch (error) {
      console.error('[RecentlyViewed] Error adding product:', error);
    }
  },

  clear() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("[RecentlyViewed] Failed to clear recently viewed products:", error);
    }
  }
};

class RecentlyViewedSection {
  constructor(element) {
    this.element = element;
    this.viewedProducts = [];
    this.sliderEl = null;
    this.limitProducts = 6;
    this.viewedProductSlider = null;

    this.load();
  }

  load() {
    // Get products from storage, excluding the product on the current page —
    // product.js may have already tracked it by the time this runs, and it's
    // already on screen, so it shouldn't also show up in its own slider.
    const currentHandle = this.#getCurrentProductHandle();
    this.viewedProducts = RecentlyViewedStorage.get().filter((handle) => handle !== currentHandle);

    this.sliderEl = this.element.querySelector("[data-recently-viewed-slider]");
    const limitEl = this.element.querySelector("[data-recently-viewed-limit]");
    this.limitProducts = limitEl ? parseInt(limitEl.getAttribute("data-recently-viewed-limit")) : 6;

    // Limit to configured amount
    this.viewedProducts = this.viewedProducts.slice(0, this.limitProducts);

    // Get the parent section element to hide/show the entire section including title
    const parentSection = this.element.closest('section');

    if (this.sliderEl && this.viewedProducts.length > 0) {
      if (parentSection) {
        parentSection.classList.remove("hidden");
        parentSection.classList.add("block");
      }
      this.addProductsToSlider();
    } else {
      if (parentSection) {
        parentSection.classList.add("hidden");
        parentSection.classList.remove("block");
      }
    }
  }

  initSlider() {
    this.viewedProductSlider = new Slider(this.sliderEl);
  }

  #getCurrentProductHandle() {
    return window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1] ?? null;
  }

  async addProductsToSlider() {
    const listEl = this.sliderEl.querySelector('[data-slider-list]');
    if (!listEl) return;

    // Fetch and render products
    for (const handle of this.viewedProducts) {
      const slide = document.createElement("div");
      slide.setAttribute('data-slider-item', '');
      slide.className = 'collection-slider__slide shrink-0';

      // Create placeholder while loading
      slide.innerHTML = `
        <div class="animate-pulse bg-gray-200 rounded" style="height: 300px;"></div>
      `;
      listEl.appendChild(slide);

      // Fetch product JSON and build card
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          const card = buildProductCard(product);
          slide.innerHTML = '';
          slide.appendChild(card);
        } else {
          slide.remove();
        }
      } catch (error) {
        console.error(`Failed to load product ${handle}:`, error);
        slide.remove();
      }
    }

    // Initialize product cards after all products are loaded
    initProductCards();

    // Initialize slider after products are rendered
    this.initSlider();
  }

  unload() {
    if (this.viewedProductSlider) {
      this.viewedProductSlider = null;
    }
  }
}

// Export storage utility (read-only for display)
export { RecentlyViewedStorage };

registerThemeEditorSection("recently-viewed-section", RecentlyViewedSection);